import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getManagementTenantPreferencesOptions,
  type TenantRead,
} from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { Upload, Loader2, Palette, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

import {
  useManagementUpdatePreferences,
  useManagementPortalBackgroundUpload,
} from "@/hooks/use-portal-branding";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_PRIMARY_COLOR = "#7e57d4";
const BACKGROUND_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const BACKGROUND_MAX_SIZE = 5 * 1024 * 1024;

interface PortalBrandingDialogProps {
  tenant: TenantRead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PortalBrandingDialog({
  tenant,
  open,
  onOpenChange,
}: PortalBrandingDialogProps) {
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [colorInput, setColorInput] = useState(DEFAULT_PRIMARY_COLOR);
  const [selectedBgFile, setSelectedBgFile] = useState<File | null>(null);
  const [bgPreviewUrl, setBgPreviewUrl] = useState<string | null>(null);
  const [removeBackground, setRemoveBackground] = useState(false);

  const updatePreferencesMutation = useManagementUpdatePreferences();
  const uploadBackgroundMutation = useManagementPortalBackgroundUpload();

  const { data: preferences, isLoading: isLoadingPreferences } = useQuery({
    ...getManagementTenantPreferencesOptions({
      ...cellRequestOptions(tenant.deployment_cell),
      path: { tenant_id: tenant.id },
    }),
    enabled: open,
  });

  // Sync state when preferences load
  useEffect(() => {
    if (preferences) {
      const resolvedColor = preferences.portal_primary_color ?? DEFAULT_PRIMARY_COLOR;
      setPrimaryColor(resolvedColor);
      setColorInput(resolvedColor);
    }
  }, [preferences]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (bgPreviewUrl) {
        URL.revokeObjectURL(bgPreviewUrl);
      }
    };
  }, [bgPreviewUrl]);

  const currentBgUrl = preferences?.portal_background_url;

  const handleColorPickerChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPrimaryColor(e.target.value);
    setColorInput(e.target.value);
  };

  const handleColorInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setColorInput(value);
    if (HEX_COLOR_REGEX.test(value)) {
      setPrimaryColor(value);
    }
  };

  const handleBgFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!BACKGROUND_ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please select a PNG, JPEG, or WebP file.");
      return;
    }

    if (file.size > BACKGROUND_MAX_SIZE) {
      toast.error("File size must be less than 5MB.");
      return;
    }

    if (bgPreviewUrl) {
      URL.revokeObjectURL(bgPreviewUrl);
    }
    setSelectedBgFile(file);
    setRemoveBackground(false);
    const objectUrl = URL.createObjectURL(file);
    setBgPreviewUrl(objectUrl);
  };

  const handleRemoveBackground = () => {
    if (bgPreviewUrl) {
      URL.revokeObjectURL(bgPreviewUrl);
    }
    setSelectedBgFile(null);
    setBgPreviewUrl(null);
    setRemoveBackground(true);
  };

  const handleSave = async () => {
    if (!HEX_COLOR_REGEX.test(colorInput)) {
      toast.error("Please enter a valid hex color (e.g., #7e57d4).");
      return;
    }

    try {
      // Upload background image if selected
      if (selectedBgFile) {
        await uploadBackgroundMutation.mutateAsync({
          file: selectedBgFile,
          tenantId: tenant.id,
          deploymentCell: tenant.deployment_cell,
        });
      }

      // Update preferences (color, and optionally remove background)
      await updatePreferencesMutation.mutateAsync({
        tenantId: tenant.id,
        deploymentCell: tenant.deployment_cell,
        portalPrimaryColor: primaryColor,
        portalBackgroundUrl: removeBackground ? "none" : undefined,
      });

      handleClose();
    } catch {
      // Errors handled by mutation callbacks
    }
  };

  const handleClose = () => {
    if (bgPreviewUrl) {
      URL.revokeObjectURL(bgPreviewUrl);
    }
    setSelectedBgFile(null);
    setBgPreviewUrl(null);
    setRemoveBackground(false);
    const savedColor = preferences?.portal_primary_color ?? DEFAULT_PRIMARY_COLOR;
    setPrimaryColor(savedColor);
    setColorInput(savedColor);
    onOpenChange(false);
  };

  const isLoading =
    updatePreferencesMutation.isPending || uploadBackgroundMutation.isPending;

  const currentColor = preferences?.portal_primary_color ?? DEFAULT_PRIMARY_COLOR;
  const hasColorChanged = primaryColor !== currentColor;
  const hasChanges = hasColorChanged || selectedBgFile !== null || removeBackground;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Portal Branding</DialogTitle>
          <DialogDescription>
            Customize the self-service portal appearance for this tenant.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          {isLoadingPreferences ? (
            <>
              <div className="flex flex-col gap-3">
                <Skeleton className="h-5 w-28" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-14 rounded" />
                  <Skeleton className="h-10 w-32 rounded" />
                  <Skeleton className="h-10 flex-1 rounded" />
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-32 w-full rounded-lg" />
                <Skeleton className="h-8 w-32 rounded" />
              </div>
            </>
          ) : (
            <>
              {/* Primary Color */}
              <div className="flex flex-col gap-3">
                <Label htmlFor="portal-color-picker" className="flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Primary Color
                </Label>
                <div className="flex items-center gap-3">
                  <input
                    id="portal-color-picker"
                    type="color"
                    value={primaryColor}
                    onChange={handleColorPickerChange}
                    disabled={isLoading}
                    className="h-10 w-14 cursor-pointer rounded border p-1"
                  />
                  <Input
                    value={colorInput}
                    onChange={handleColorInputChange}
                    placeholder="#7e57d4"
                    disabled={isLoading}
                    className="w-32 font-mono"
                    maxLength={7}
                  />
                  <div
                    className="h-10 flex-1 rounded border"
                    style={{
                      background: `linear-gradient(118deg, ${primaryColor} 0%, ${darkenColor(primaryColor, 0.7)} 100%)`,
                    }}
                  />
                </div>
                {!HEX_COLOR_REGEX.test(colorInput) && colorInput.length > 0 && (
                  <p className="text-sm text-destructive">
                    Enter a valid hex color (e.g., #7e57d4)
                  </p>
                )}
              </div>

              {/* Background Image */}
              <div className="flex flex-col gap-3">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Hero Background Image
                </Label>

                {/* Preview */}
                <div className="relative h-32 w-full overflow-hidden rounded-lg border">
                  {removeBackground ? (
                    <div
                      className="flex h-full w-full items-center justify-center"
                      style={{
                        background: `linear-gradient(118deg, ${primaryColor} 0%, ${darkenColor(primaryColor, 0.7)} 100%)`,
                      }}
                    >
                      <span className="text-sm text-white/70">
                        Gradient only (no image)
                      </span>
                    </div>
                  ) : bgPreviewUrl ?? currentBgUrl ? (
                    <div className="relative h-full w-full">
                      <img
                        src={bgPreviewUrl ?? currentBgUrl ?? ""}
                        alt="Background preview"
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/20" />
                      {(bgPreviewUrl ?? currentBgUrl) && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute right-2 top-2 h-7 w-7"
                          onClick={handleRemoveBackground}
                          disabled={isLoading}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div
                      className="flex h-full w-full items-center justify-center"
                      style={{
                        background: `linear-gradient(118deg, ${primaryColor} 0%, ${darkenColor(primaryColor, 0.7)} 100%)`,
                      }}
                    >
                      <span className="text-sm text-white/70">
                        No background image set
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => {
                      document.getElementById("bg-file-input")?.click();
                    }}
                    type="button"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {selectedBgFile ? "Change Image" : "Upload Image"}
                  </Button>
                  {selectedBgFile && (
                    <span className="self-center text-sm text-muted-foreground">
                      {selectedBgFile.name}
                    </span>
                  )}
                </div>
                <input
                  id="bg-file-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleBgFileSelect}
                  className="hidden"
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Recommended: 1920x600px or larger. PNG, JPEG, or WebP. Max 5MB.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isLoading || isLoadingPreferences || !hasChanges}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function darkenColor(hex: string, factor: number): string {
  if (!HEX_COLOR_REGEX.test(hex)) return hex;
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}
